import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  CRM_CONTACT_TYPES,
  CRM_LEAD_SOURCES,
  CRM_PIPELINE_STATUSES,
} from '@bcc/shared';
import type { CrmContactWithMeta } from '@bcc/shared';
import { PhoneField } from '../PhoneField';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD, SITE_SELECT_OPTIONS } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';

const SOURCE_VALUES = Object.values(CRM_LEAD_SOURCES);

type Props = {
  token: string;
  contact: CrmContactWithMeta | null;
  open: boolean;
  onClose: () => void;
  onSaved: (contact: CrmContactWithMeta) => void;
};

const STATUS_VALUES = Object.values(CRM_PIPELINE_STATUSES);

export function EditContactModal({ token, contact, open, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [full_name, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [desired_commitment, setDesiredCommitment] = useState('');
  const [accreditedMode, setAccreditedMode] = useState<'yes' | 'no' | 'unknown'>('unknown');
  const [type, setType] = useState<string>(CRM_CONTACT_TYPES.INVESTOR);
  const [status, setStatus] = useState<string>(CRM_PIPELINE_STATUSES.NEW);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [leadSource, setLeadSource] = useState<string>('');

  useEffect(() => {
    if (!open || !contact) return;
    setFullName(contact.full_name ?? '');
    setEmail(contact.email ?? '');
    setPhone(contact.phone ?? '');
    setDesiredCommitment(contact.desired_commitment ?? '');
    if (contact.accredited_investor === true) setAccreditedMode('yes');
    else if (contact.accredited_investor === false) setAccreditedMode('no');
    else setAccreditedMode('unknown');
    setType(contact.type ?? CRM_CONTACT_TYPES.INVESTOR);
    setStatus(contact.status ?? CRM_PIPELINE_STATUSES.NEW);
    setNotes(contact.notes ?? '');
    setTags((contact.tags ?? []).join(', '));
    setLeadSource(contact.lead_source ?? '');
  }, [open, contact]);

  if (!open || !contact) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!contact) return;
    setLoading(true);
    try {
      const tagArr = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const accredited_investor =
        accreditedMode === 'yes' ? true : accreditedMode === 'no' ? false : null;
      const payload = {
        full_name,
        email: email.trim() || null,
        phone: phone.trim() || null,
        desired_commitment: desired_commitment.trim() || null,
        accredited_investor,
        type,
        status,
        notes: notes || null,
        tags: tagArr,
        lead_source: leadSource || null,
      };
      const res = await apiFetch<{ contact: CrmContactWithMeta }>(`/api/crm/contacts/${contact.id}`, {
        method: 'PATCH',
        token,
        body: JSON.stringify(payload),
      });
      onSaved(res.contact);
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className={`max-h-[90vh] w-full max-w-lg overflow-y-auto p-6 shadow-2xl ${SITE_GLASS_CARD}`}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-[var(--home-text-on-dark)]">Edit CRM contact</h2>
          <button
            type="button"
            className="text-[var(--home-muted-on-dark)] hover:text-[var(--home-text-on-dark)]"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <form className="space-y-4" onSubmit={onSubmit}>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Full name / entity</span>
            <input
              required
              value={full_name}
              onChange={(e) => setFullName(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Phone</span>
            <PhoneField
              value={phone}
              onChange={setPhone}
              selectClassName={`${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS} min-w-[7rem] shrink-0 cursor-pointer py-2 font-body text-xs`}
              inputClassName={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Desired commitment</span>
            <input
              value={desired_commitment}
              onChange={(e) => setDesiredCommitment(e.target.value)}
              placeholder="e.g. $250k–$500k"
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Accredited investor</span>
            <select
              value={accreditedMode}
              onChange={(e) => setAccreditedMode(e.target.value as 'yes' | 'no' | 'unknown')}
              className={`w-full ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
            >
              <option value="unknown">Unknown / not stated</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Type</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={`w-full capitalize ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
              >
                <option value={CRM_CONTACT_TYPES.INVESTOR}>{CRM_CONTACT_TYPES.INVESTOR}</option>
                <option value={CRM_CONTACT_TYPES.LEAD}>{CRM_CONTACT_TYPES.LEAD}</option>
                <option value={CRM_CONTACT_TYPES.BROKER}>{CRM_CONTACT_TYPES.BROKER}</option>
                <option value={CRM_CONTACT_TYPES.OWNER}>{CRM_CONTACT_TYPES.OWNER}</option>
              </select>
            </label>
            <label className="block space-y-1 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Pipeline</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={`w-full capitalize ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
              >
                {STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Lead source</span>
            <select
              value={leadSource}
              onChange={(e) => setLeadSource(e.target.value)}
              className={`w-full capitalize ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
            >
              <option value="">—</option>
              {SOURCE_VALUES.map((s) => (
                <option key={s} value={s}>
                  {String(s).replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Tags (comma-separated)</span>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className={`w-full font-mono text-[11px] ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-xs">
            <span className="text-[var(--home-muted-on-dark)]">Notes (Markdown supported)</span>
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full text-sm ${SITE_FORM_CONTROL}`}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2 text-sm text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] hover:text-[var(--home-text-on-dark)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105 disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
